import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { Search, Info, Loader2, ArrowLeft } from 'lucide-react'; // Added ArrowLeft
import OfferCard from '../components/OfferCard';
import OfferDetailView from '../components/OfferDetailView';
import MobileOfferDrawer from '../components/MobileOfferDrawer';
import ModernMultiSelect from '../components/ModernMultiSelect'; // Changed from ModernSelect
import ModernPagination from '../components/ModernPagination';
import SearchBox from '../components/SearchBox';
import { supabase } from '../supabaseClient';
import { track } from '../lib/analytics';
import PremiumModal from '../components/PremiumModal';
import { useAuth } from '../context/AuthContext';
import { useEnsureEmbedding } from '../hooks/useEnsureEmbedding';

const ITEMS_PER_PAGE = 20;

const OffersPage = () => {
    const { user } = useAuth(); // Get user from AuthContext
    const [searchParams, setSearchParams] = useSearchParams();
    const { id: routeOfferId } = useParams(); // Get ID from route
    const navigate = useNavigate();
    const continent = searchParams.get('continent');

    // Core State
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [favorites, setFavorites] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('favorite_offers') || '[]');
        } catch {
            return [];
        }
    });

    // Viewed offers tracking
    const [viewedOffers, setViewedOffers] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('viewed_offers') || '[]');
        } catch {
            return [];
        }
    });

    // Filtres : la recherche, les pays et les secteurs vivent dans l'URL (source de vérité),
    // pour que « Offres » remette tout à zéro et que les liens soient partageables.
    const searchQuery = searchParams.get('search') || '';
    const urlCountryKey = searchParams.get('country') || '';
    const urlSectorKey = searchParams.get('sector') || '';
    const [selectedCountry, setSelectedCountry] = useState(urlCountryKey ? urlCountryKey.split('|') : []);
    const [selectedSector, setSelectedSector] = useState(urlSectorKey ? urlSectorKey.split('|') : []);

    useEffect(() => {
        setSelectedCountry(urlCountryKey ? urlCountryKey.split('|') : []);
    }, [urlCountryKey]);
    useEffect(() => {
        setSelectedSector(urlSectorKey ? urlSectorKey.split('|') : []);
    }, [urlSectorKey]);

    const updateParams = (changes) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([k, v]) => {
            if (v) next.set(k, v);
            else next.delete(k);
        });
        setSearchParams(next, { replace: true });
    };
    const handleCountryChange = (vals) => updateParams({ country: vals.join('|') });
    const handleSectorChange = (vals) => updateParams({ sector: vals.join('|') });

    // Suggestion choisie dans la barre : pays et secteur remplacent le filtre, les autres
    // suggestions deviennent un mot-clé de recherche.
    const handleSuggestionPick = (s) => {
        if (s.kind === 'pays') updateParams({ search: null, country: s.label });
        else if (s.kind === 'secteur') updateParams({ search: null, sector: s.label });
        else updateParams({ search: s.label });
    };

    const requestIdRef = useRef(0);
    const [selectedRegion, setSelectedRegion] = useState([]);
    const [selectedDuration, setSelectedDuration] = useState([]);

    // Filter Options (fetched from DB)
    const [countries, setCountries] = useState([]);
    const [sectors, setSectors] = useState([]);
    const [regions, setRegions] = useState([]);
    const [durations, setDurations] = useState([]);
    const [countryCodeMap, setCountryCodeMap] = useState({});

    // Pagination State
    const [page, setPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    // UI State
    const [showCvMatchingInfo, setShowCvMatchingInfo] = useState(false);
    const [isCvMatchActive, setIsCvMatchActive] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [isPremium, setIsPremium] = useState(false);
    const [offresPlusTeaser, setOffresPlusTeaser] = useState(null);

    useEffect(() => {
        const checkPremium = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
                const { data } = await supabase.from('profiles').select('is_premium').eq('id', session.user.id).single();
                setIsPremium(data?.is_premium || false);
            }
        };
        checkPremium();
    }, []);

    // Sync Favorites & Viewed Offers with Supabase
    useEffect(() => {
        if (!user) return;

        const fetchUserActivities = async () => {
            try {
                const { data, error } = await supabase
                    .from('user_offer_activities')
                    .select('offer_id, is_favorite, is_viewed')
                    .eq('user_id', user.id);

                if (error) {
                    console.error("Error fetching user activities:", error);
                    return;
                }

                if (data) {
                    const dbFavorites = data.filter(item => item.is_favorite).map(item => item.offer_id);
                    const dbViewed = data.filter(item => item.is_viewed).map(item => item.offer_id);

                    if (dbFavorites.length > 0) setFavorites(dbFavorites);
                    if (dbViewed.length > 0) setViewedOffers(dbViewed);
                }
            } catch (err) {
                console.error("Error syncing user activities:", err);
            }
        };

        fetchUserActivities();
    }, [user]);

    // Toggle CV Matching - Premium Feature
    const { ensureEmbedding, isUpdating: isEmbeddingUpdating } = useEnsureEmbedding();

    const toggleCvMatching = async () => {
        if (isPremium) {
            // If turning ON, ensure embedding is ready
            if (!isCvMatchActive) {
                const embedding = await ensureEmbedding();
                if (embedding) {
                    setIsCvMatchActive(true);
                }
            } else {
                setIsCvMatchActive(false);
            }
        } else {
            setShowPremiumModal(true);
        }
    };

    // Infinite scroll sentinel ref
    const sentinelRef = useRef(null);

    // Check if mobile
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Reset pagination when filters change
    useEffect(() => {
        setPage(0);
        setOffers([]);
        setHasMore(true);
    }, [selectedCountry, selectedSector, selectedRegion, selectedDuration, searchQuery, continent, isCvMatchActive]);

    // Fetch filter options on mount
    useEffect(() => {
        const fetchFilterOptions = async () => {
            try {
                const { data: countryData } = await supabase
                    .from('offres_vie_bf_public')
                    .select('pays, country_code')
                    .eq('url_live', 'active')
                    .not('pays', 'is', null);
                if (countryData) {
                    const unique = [...new Set(countryData.map(d => d.pays))].sort();
                    setCountries(unique);

                    const map = {};
                    countryData.forEach(c => {
                        if (c.country_code) map[c.country_code] = c.pays;
                    });
                    setCountryCodeMap(map);
                }

                const { data: sectorData } = await supabase
                    .from('offres_vie_bf_public')
                    .select('parent_label_fr')
                    .eq('url_live', 'active')
                    .not('parent_label_fr', 'is', null);
                if (sectorData) {
                    const unique = [...new Set(sectorData.map(d => d.parent_label_fr).filter(s => s && s.trim()))].sort();
                    setSectors(unique);
                }

                try {
                    const { data: regionData } = await supabase
                        .from('offres_vie_bf_public')
                        .select('region')
                        .eq('url_live', 'active')
                        .not('region', 'is', null);
                    if (regionData && regionData.length > 0) {
                        const unique = [...new Set(regionData.map(d => d.region))].sort();
                        setRegions(unique);
                    }
                } catch (e) {
                    console.log('Region column not available');
                }

                // Fetch Durations
                try {
                    const { data: durationData } = await supabase
                        .from('offres_vie_bf_public')
                        .select('duree')
                        .eq('url_live', 'active')
                        .not('duree', 'is', null);
                    if (durationData && durationData.length > 0) {
                        const unique = [...new Set(durationData.map(d => d.duree).filter(d => d && d.toString().trim()))]
                            .sort((a, b) => {
                                const numA = parseInt(a);
                                const numB = parseInt(b);
                                if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                                return String(a).localeCompare(String(b));
                            });
                        setDurations(unique);
                    }
                } catch (e) {
                    console.log('Duration column not available');
                }
            } catch (err) {
                console.error('Error fetching filter options:', err);
            }
        };
        fetchFilterOptions();
    }, []);

    // Notification Logic: Apply filters from profile if source=notifications
    useEffect(() => {
        const checkSource = async () => {
            const source = searchParams.get('source');
            if (source === 'notifications' && user) {
                const { data } = await supabase
                    .from('profiles')
                    .select('preferred_countries, preferred_sectors')
                    .eq('id', user.id)
                    .single();

                if (data) {
                    // Map codes based on available map OR inverted hardcoded map fallback
                    if (data.preferred_countries && data.preferred_countries.length > 0) {
                        // Build complete map: combination of DB map + hardcoded fallback
                        const hardcodedMapTarget = {
                            'ALLEMAGNE': 'DE', 'BELGIQUE': 'BE', 'ESPAGNE': 'ES', 'ITALIE': 'IT',
                            'PORTUGAL': 'PT', 'SUISSE': 'CH', 'ROYAUME-UNI': 'GB', 'CANADA': 'CA',
                            'ETATS-UNIS': 'US', 'AUSTRALIE': 'AU', 'JAPON': 'JP', 'CHINE': 'CN',
                            'COREE DU SUD': 'KR', 'SINGAPOUR': 'SG', 'EMIRATS ARABES UNIS': 'AE',
                            'MAROC': 'MA', 'SENEGAL': 'SN', 'BRESIL': 'BR', 'MEXIQUE': 'MX',
                            'VIETNAM': 'VN', 'VIET NAM': 'VN', 'INDE': 'IN', 'POLOGNE': 'PL',
                            'ROUMANIE': 'RO', 'PAYS-BAS': 'NL', 'HONGRIE': 'HU',
                            'REPUBLIQUE TCHEQUE': 'CZ', 'TCHEQUIE': 'CZ', 'TCHEQUIE / REPUBLIQUE TCHEQUE': 'CZ',
                            'AUTRICHE': 'AT', 'SUEDE': 'SE', 'NORVEGE': 'NO', 'FINLANDE': 'FI',
                            'DANEMARK': 'DK', 'IRLANDE': 'IE', 'PHILIPPINES': 'PH', 'THAILANDE': 'TH',
                            'INDONESIE': 'ID', 'MALAISIE': 'MY', 'TURQUIE': 'TR', 'GRECE': 'GR',
                            'LUXEMBOURG': 'LU', 'CAMEROUN': 'CM', "COTE D'IVOIRE": 'CI',
                            'LAOS': 'LA', 'EGYPTE': 'EG', 'AFRIQUE DU SUD': 'ZA', 'COLOMBIE': 'CO',
                            'PEROU': 'PE', 'CHILI': 'CL', 'ARGENTINE': 'AR', 'TAIWAN': 'TW',
                            'ARABIE SAOUDITE': 'SA', 'QATAR': 'QA', 'KOWEIT': 'KW', 'LIBAN': 'LB',
                            'JORDANIE': 'JO', 'MAURICE': 'MU', 'KENYA': 'KE', 'MADAGASCAR': 'MG',
                            'CONGO': 'CG', 'REPUBLIQUE DU CONGO': 'CG',
                            'REPUBLIQUE DEMOCRATIQUE DU CONGO': 'CD', 'RDC': 'CD',
                            'ANGOLA': 'AO', 'NIGERIA': 'NG', 'TUNISIE': 'TN', 'AZERBAIDJAN': 'AZ'
                        };
                        // Invert hardcoded map
                        const codeToName = {};
                        Object.entries(hardcodedMapTarget).forEach(([name, code]) => {
                            codeToName[code] = name; // e.g. 'CA': 'CANADA' matches filter format
                        });

                        // Merge: prefer DB map, fallback to hardcoded
                        const names = data.preferred_countries.map(pref => {
                            // Handle both codes (2 chars) AND full names (from ProfilePage)
                            if (pref && pref.length === 2) {
                                return countryCodeMap[pref] || codeToName[pref] || null;
                            }
                            return pref;
                        }).filter(Boolean);

                        if (names.length > 0) {
                            // Correct casing by finding match in 'countries' list
                            const correctedNames = names.map(n =>
                                countries.find(c => c.toUpperCase() === n.toUpperCase()) || n
                            );
                            setSelectedCountry(correctedNames);
                        }
                    }
                    // Sectors are already names
                    if (data.preferred_sectors && data.preferred_sectors.length > 0) {
                        setSelectedSector(data.preferred_sectors);
                    }
                }
            }
        };
        checkSource();
    }, [user, searchParams, countryCodeMap, countries]);

    // Country code mapping
    const getCountryCode = (name, code) => {
        if (code && code.length === 2 && code !== 'null') {
            return code.toUpperCase();
        }
        const normalizedName = name ? name.toUpperCase().trim() : '';
        const map = {
            'ALLEMAGNE': 'DE', 'BELGIQUE': 'BE', 'ESPAGNE': 'ES', 'ITALIE': 'IT',
            'PORTUGAL': 'PT', 'SUISSE': 'CH', 'ROYAUME-UNI': 'GB', 'CANADA': 'CA',
            'ETATS-UNIS': 'US', 'AUSTRALIE': 'AU', 'JAPON': 'JP', 'CHINE': 'CN',
            'COREE DU SUD': 'KR', 'SINGAPOUR': 'SG', 'EMIRATS ARABES UNIS': 'AE',
            'MAROC': 'MA', 'SENEGAL': 'SN', 'BRESIL': 'BR', 'MEXIQUE': 'MX',
            'VIETNAM': 'VN', 'VIET NAM': 'VN', 'INDE': 'IN', 'POLOGNE': 'PL',
            'ROUMANIE': 'RO', 'PAYS-BAS': 'NL', 'HONGRIE': 'HU',
            'REPUBLIQUE TCHEQUE': 'CZ', 'TCHEQUIE': 'CZ', 'TCHEQUIE / REPUBLIQUE TCHEQUE': 'CZ',
            'AUTRICHE': 'AT', 'SUEDE': 'SE', 'NORVEGE': 'NO', 'FINLANDE': 'FI',
            'DANEMARK': 'DK', 'IRLANDE': 'IE', 'PHILIPPINES': 'PH', 'THAILANDE': 'TH',
            'INDONESIE': 'ID', 'MALAISIE': 'MY', 'TURQUIE': 'TR', 'GRECE': 'GR',
            'LUXEMBOURG': 'LU', 'CAMEROUN': 'CM', "COTE D'IVOIRE": 'CI',
            'LAOS': 'LA', 'EGYPTE': 'EG', 'AFRIQUE DU SUD': 'ZA', 'COLOMBIE': 'CO',
            'PEROU': 'PE', 'CHILI': 'CL', 'ARGENTINE': 'AR', 'TAIWAN': 'TW',
            'ARABIE SAOUDITE': 'SA', 'QATAR': 'QA', 'KOWEIT': 'KW', 'LIBAN': 'LB',
            'JORDANIE': 'JO', 'MAURICE': 'MU', 'KENYA': 'KE', 'MADAGASCAR': 'MG',
            'CONGO': 'CG', 'REPUBLIQUE DU CONGO': 'CG',
            'REPUBLIQUE DEMOCRATIQUE DU CONGO': 'CD', 'RDC': 'CD',
            'ANGOLA': 'AO', 'NIGERIA': 'NG', 'TUNISIE': 'TN', 'AZERBAIDJAN': 'AZ'
        };
        return map[normalizedName] || 'FR';
    };

    // Fetch offers
    const fetchOffers = useCallback(async (pageNum, append = false) => {
        const requestId = ++requestIdRef.current;
        const isStale = () => requestId !== requestIdRef.current;
        if (append) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }

        try {
            let data = [];
            let count = 0;

            if (isCvMatchActive) {
                const { data: { user } } = await supabase.auth.getUser();
                if (!user) {
                    alert("Veuillez vous connecter pour utiliser le matching IA.");
                    setIsCvMatchActive(false);
                    setLoading(false);
                    return;
                }

                const { data: profileData, error: profileError } = await supabase
                    .from('profiles')
                    .select('embedding')
                    .eq('id', user.id)
                    .single();

                const userEmbedding = profileData?.embedding;

                if (profileError || !userEmbedding) {
                    alert("Aucun profil analysé trouvé. Veuillez uploader un CV dans votre profil.");
                    setIsCvMatchActive(false);
                    setLoading(false);
                    return;
                }

                console.log("🔍 Calling CV Matching (Free) with embedding len:", userEmbedding.length);
                const { data: rpcData, error: rpcError } = await supabase.rpc('cv_matching_free', {
                    embedding: userEmbedding,
                    match_threshold: 0.1, // Lowered from 0.5 to 0.1
                    match_count: 50
                });

                if (rpcError) {
                    console.error("CV Match Error (Free):", rpcError);
                    throw rpcError;
                }
                console.log("✅ CV Matching (Free) Results:", rpcData?.length || 0, "offers found.");
                track('feature_used', { feature: 'cv_match', results_count: rpcData?.length || 0 });
                data = rpcData || [];
                count = data.length;
                setHasMore(false); // CV matching returns all at once

            } else {
                // Determine if specifically fetching by ID (Full Screen Mode)
                if (routeOfferId && !append) {
                    // Single Offer Fetch
                    let query = supabase
                        .from('offres_vie_bf_public')
                        .select('id_offre, intitule_poste, entreprise, pays, ville, duree, date_publication, offre_complete, lien_offre, specialization_label_fr, parent_label_fr, langues_requises, country_code, url_live, sidebar_summary, company_html, mission_html, profile_html')
                        .eq('id_offre', routeOfferId)
                        .single();

                    const res = await query;
                    if (res.error) throw res.error;
                    data = [res.data]; // Wrap in array for mapping
                    count = 1;

                } else {
                    // Normal List Fetch
                    let query = supabase
                        .from('offres_vie_bf_public')
                        .select('id_offre, intitule_poste, entreprise, pays, ville, duree, date_publication, offre_complete, lien_offre, specialization_label_fr, parent_label_fr, langues_requises, country_code, url_live, sidebar_summary, company_html, mission_html, profile_html', { count: 'exact' })
                        .eq('url_live', 'active')
                        .order('date_publication', { ascending: false })
                        .range(pageNum * ITEMS_PER_PAGE, (pageNum + 1) * ITEMS_PER_PAGE - 1);

                    if (selectedCountry.length > 0) query = query.in('pays', selectedCountry);
                    if (selectedSector.length > 0) query = query.in('parent_label_fr', selectedSector);
                    if (selectedRegion.length > 0) query = query.in('region', selectedRegion);
                    if (selectedDuration.length > 0) query = query.in('duree', selectedDuration);

                    // Filter by Date (Notifications)
                    const since = searchParams.get('since');
                    if (since && searchParams.get('source') === 'notifications') {
                        query = query.gt('date_publication', since);
                    }

                    if (searchQuery) {
                        query = query.or(`intitule_poste.ilike.%${searchQuery}%,entreprise.ilike.%${searchQuery}%,pays.ilike.%${searchQuery}%,ville.ilike.%${searchQuery}%,offre_complete.ilike.%${searchQuery}%`);
                    }

                    const res = await query;
                    if (res.error) throw res.error;
                    data = res.data;
                    count = res.count;
                    // Check if there are more pages
                    setHasMore((pageNum + 1) * ITEMS_PER_PAGE < count);

                    const hasFilters = searchQuery || selectedCountry.length || selectedSector.length || selectedRegion.length || selectedDuration.length;
                    if (hasFilters && pageNum === 0 && !append) {
                        track('search_performed', {
                            query: searchQuery || null,
                            countries: selectedCountry,
                            sectors: selectedSector,
                            regions: selectedRegion,
                            durations: selectedDuration,
                            results_count: count,
                        });
                    }
                }
            }

            if (isStale()) return;
            if (count !== null) setTotalCount(count);

            const mappedOffers = (data || []).map(off => ({
                id: off.id_offre,
                title: off.intitule_poste || 'Poste sans titre',
                company: off.entreprise,
                countryCode: getCountryCode(off.pays, off.country_code),
                country: off.pays || 'France',
                city: off.ville || 'TBD',
                sector: off.parent_label_fr || off.specialization_label_fr || 'Général',
                badge: off.specialization_label_fr ? 'Expertise' : 'V.I.E',
                duree: off.duree,
                duration: off.duree ? `${off.duree} mois` : '12 mois',
                date: off.date_publication ? new Date(off.date_publication).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : 'Récemment',
                summary: off.sidebar_summary || (off.offre_complete || '').substring(0, 160) + '...',
                description: off.offre_complete,
                companyHtml: off.company_html,
                missionHtml: off.mission_html,
                profileHtml: off.profile_html,
                link: off.lien_offre || '#',
                langues_requises: off.langues_requises,
                details: {
                    entreprise: off.description_entreprise || '',
                    poste: off.offre_complete,
                    profil: off.description_candidat || '',
                    langues: off.langues_requises,
                }
            }));

            if (isStale()) return;
            if (append) {
                setOffers(prev => [...prev, ...mappedOffers]);
            } else {
                setOffers(mappedOffers);
                if (mappedOffers.length > 0) {
                    setSelectedOffer(mappedOffers[0]);
                }
            }
        } catch (err) {
            if (isStale()) return;
            console.error('Error fetching offers:', err);
            if (!append) setOffers([]);
        } finally {
            if (!isStale()) {
                setLoading(false);
                setLoadingMore(false);
            }
        }
    }, [selectedCountry, selectedSector, selectedRegion, selectedDuration, searchQuery, continent, isCvMatchActive, routeOfferId]);

    // Initial fetch — mobile: append (infinite scroll), desktop: replace
    useEffect(() => {
        fetchOffers(page, isMobile && page > 0);
    }, [page, fetchOffers, isMobile]);

    // Recherche sans résultat Business France : on tease les offres Offres+ correspondantes
    useEffect(() => {
        const term = searchQuery.trim().replace(/[,()]/g, ' ');
        if (loading || offers.length > 0 || !term || isCvMatchActive) {
            setOffresPlusTeaser(null);
            return;
        }

        let cancelled = false;
        supabase
            .rpc('count_offres_plus_matching', { p_term: term })
            .then(({ data, error }) => {
                if (cancelled) return;
                const row = data?.[0];
                setOffresPlusTeaser(!error && row?.match_count > 0
                    ? { count: row.match_count, samples: row.samples || [] }
                    : null);
            });

        return () => { cancelled = true; };
    }, [searchQuery, loading, offers.length, isCvMatchActive]);

    // Infinite scroll observer (mobile only)
    useEffect(() => {
        if (!isMobile || !hasMore || loading || loadingMore) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore) {
                    setPage(prev => prev + 1);
                }
            },
            { threshold: 0.1 }
        );

        if (sentinelRef.current) {
            observer.observe(sentinelRef.current);
        }

        return () => observer.disconnect();
    }, [isMobile, hasMore, loading, loadingMore]);

    // Desktop: Scroll to top on page change
    useEffect(() => {
        if (!isMobile) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [page, isMobile]);

    const toggleFavorite = async (offerId) => {
        // Optimistic UI update
        const isNowFavorite = !favorites.includes(offerId);
        if (isNowFavorite) track('offer_favorited', { offer_id: offerId, source: 'bf' });
        setFavorites(prev => {
            const newFavs = prev.includes(offerId)
                ? prev.filter(id => id !== offerId)
                : [...prev, offerId];
            localStorage.setItem('favorite_offers', JSON.stringify(newFavs));
            return newFavs;
        });

        if (user) {
            try {
                // Also pass is_viewed to avoid overwriting it with null/default if it existed
                const isViewed = viewedOffers.includes(offerId);

                const { error } = await supabase
                    .from('user_offer_activities')
                    .upsert({
                        user_id: user.id,
                        offer_id: offerId,
                        is_favorite: isNowFavorite,
                        is_viewed: isViewed,
                        updated_at: new Date()
                    }, { onConflict: 'user_id, offer_id' });

                if (error) console.error("Error updating favorite in DB:", error);
            } catch (err) {
                console.error("Exception updating favorite:", err);
            }
        }
    };

    const markAsViewed = async (offerId) => {
        if (!viewedOffers.includes(offerId)) {
            const updated = [...viewedOffers, offerId];
            setViewedOffers(updated);
            localStorage.setItem('viewed_offers', JSON.stringify(updated));

            if (user) {
                try {
                    // We need to be careful not to overwrite is_favorite if it exists.
                    // But upsert with just is_viewed might nullify is_favorite if we don't fetch first?
                    // No, existing rows: valid columns are updated, others kept? 
                    // Postgres UPSERT (ON CONFLICT DO UPDATE) usually requires specifying what to update.
                    // Supabase .upsert() handles this but if we only pass partial data, does it merge?
                    // Default behavior of upsert in Supabase JS client REPLACES the row unless `ignoreDuplicates` is true (which skips).
                    // Wait, standard upsert replaces. To merge, we might need to handle it differently or use proper onConflict logic.
                    // Actually, Supabase `upsert` roughly maps to:
                    // INSERT ... ON CONFLICT (id) DO UPDATE SET ...
                    // If we only provide `is_viewed`, it might set `is_favorite` to null if not provided?
                    // Let's check Supabase behavior. Usually it updates only provided columns if we configure it right or if we use specific query.
                    // Actually, safest is to key off the primary key. 
                    // Better approach: use `update` if row exists, or standard upsert but we need to know current state to not lose data?
                    // Actually, `user_offer_activities` has `user_id`, `offer_id` as PK.
                    // If we do upsert({ user_id, offer_id, is_viewed: true }), and the row exists with is_favorite=true, 
                    // will is_favorite become null/default? Yes, if we don't include it.
                    // So we should probably just use an RPC or a robust check.
                    // OR: simply use `upsert` but we need to safeguard the other column.

                    // Actually, since we have the state locally (`favorites`), we can just pass the current state of `is_favorite` too!
                    // This is much simpler since we have the data right here.

                    const isFav = favorites.includes(offerId);

                    const { error } = await supabase
                        .from('user_offer_activities')
                        .upsert({
                            user_id: user.id,
                            offer_id: offerId,
                            is_viewed: true,
                            is_favorite: isFav, // Pass current favorite state to preserve it
                            updated_at: new Date()
                        }, { onConflict: 'user_id, offer_id' });

                    if (error) console.error("Error updating viewed status in DB:", error);
                } catch (err) {
                    console.error("Exception updating viewed status:", err);
                }
            }
        }
    };

    const handleOfferClick = (offer) => {
        track('offer_viewed', { offer_id: offer.id, source: 'bf', company: offer.company, country: offer.country });
        markAsViewed(offer.id);
        setSelectedOffer(offer);
        if (isMobile) {
            setMobileDrawerOpen(true);
        }
    };

    const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

    // Memoized filter options
    const countryOptions = useMemo(() => [
        ...countries.map(c => ({ value: c, label: c }))
    ], [countries]);

    const sectorOptions = useMemo(() => [
        ...sectors.map(s => ({ value: s, label: s }))
    ], [sectors]);

    const regionOptions = useMemo(() => [
        ...regions.map(r => ({ value: r, label: r }))
    ], [regions]);

    const durationOptions = useMemo(() => [
        ...durations.map(d => ({ value: d, label: `${d} mois` }))
    ], [durations]);

    return (
        <div className="min-h-screen bg-[#F0F9FF] py-6 lg:py-10 px-4 sm:px-6 lg:px-8">
            {!routeOfferId && (
                <Helmet>
                    <title>Offres de VIE en cours - Toutes les missions disponibles | Trouve ton VIE</title>
                    <meta name="description" content="1 000+ offres de Volontariat International en Entreprise actuellement ouvertes. Filtrez par pays, secteur et durée. Mises à jour quotidiennes." />
                    <link rel="canonical" href="https://trouvetonvie.fr/offres_vie" />
                    <meta property="og:title" content="Offres de VIE en cours - Toutes les missions disponibles | Trouve ton VIE" />
                    <meta property="og:description" content="1 000+ offres de Volontariat International en Entreprise actuellement ouvertes. Filtrez par pays, secteur et durée. Mises à jour quotidiennes." />
                    <meta property="og:url" content="https://trouvetonvie.fr/offres_vie" />
                    <meta property="og:type" content="website" />
                    <meta property="og:image" content="https://trouvetonvie.fr/og-image.png" />
                    <meta property="og:locale" content="fr_FR" />
                    <meta property="og:site_name" content="Trouve ton VIE" />
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content="Offres de VIE en cours - Toutes les missions disponibles | Trouve ton VIE" />
                    <meta name="twitter:description" content="1 000+ offres de Volontariat International en Entreprise actuellement ouvertes. Filtrez par pays, secteur et durée. Mises à jour quotidiennes." />
                    <meta name="twitter:image" content="https://trouvetonvie.fr/og-image.png" />
                </Helmet>
            )}
            {routeOfferId && selectedOffer?.intitule_poste && (() => {
                const title = `${selectedOffer.intitule_poste} - ${selectedOffer.entreprise} - VIE ${selectedOffer.pays} | Trouve ton VIE`;
                const description = (selectedOffer.sidebar_summary || `Offre VIE ${selectedOffer.intitule_poste} chez ${selectedOffer.entreprise} au ${selectedOffer.pays}.`).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 158);
                const url = `https://trouvetonvie.fr/offres_vie/${selectedOffer.id_offre}`;
                return (
                    <Helmet>
                        <title>{title}</title>
                        <meta name="description" content={description} />
                        <link rel="canonical" href={url} />
                        <meta property="og:title" content={title} />
                        <meta property="og:description" content={description} />
                        <meta property="og:url" content={url} />
                    </Helmet>
                );
            })()}
            <div className={`max-w-7xl mx-auto ${routeOfferId ? 'flex flex-col' : ''}`}>
                {/* Back Button for Full Screen Mode */}
                {routeOfferId && (
                    <div className="w-full mb-6">
                        <button
                            onClick={() => navigate('/offres_vie')}
                            className="flex items-center gap-2 text-slate-500 hover:text-blue-600 font-bold transition-colors bg-white px-4 py-2 rounded-xl shadow-sm border border-slate-100"
                        >
                            <ArrowLeft size={20} />
                            Retour aux offres
                        </button>
                    </div>
                )}

                {/* ========== MOBILE FILTERS (Stacked) - Hide if viewing single offer ========== */}
                {!routeOfferId && (
                    <div className="flex flex-col gap-3 mb-6 md:hidden">
                        {/* Search Bar */}
                        <SearchBox
                            wrapperClassName="w-full"
                            inputClassName="w-full pl-11 pr-4 py-3.5 rounded-full border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-100 text-sm font-medium text-slate-600 placeholder:text-slate-400"
                            iconClassName="text-slate-500"
                            placeholder="Rechercher par pays, entreprise, intitulé..."
                            initialValue={searchQuery}
                            onSubmit={(term) => updateParams({ search: term })}
                            onPick={handleSuggestionPick}
                        />

                        {/* Country Filter */}
                        <ModernMultiSelect
                            value={selectedCountry}
                            options={countryOptions}
                            onChange={handleCountryChange}
                            placeholder="Tous pays"
                            className="w-full"
                        />

                        {/* Sector Filter */}
                        <ModernMultiSelect
                            value={selectedSector}
                            options={sectorOptions}
                            onChange={handleSectorChange}
                            placeholder="Toutes spécialisations"
                            className="w-full"
                        />

                        {/* Duration Filter */}
                        {durations.length > 0 && (
                            <ModernMultiSelect
                                value={selectedDuration}
                                options={durationOptions}
                                onChange={setSelectedDuration}
                                placeholder="Toutes durées"
                                className="w-full"
                            />
                        )}

                        {/* CV Matching Button - Premium Style */}
                        <div
                            className={`w-full rounded-2xl py-3 px-4 border-2 transition-all cursor-pointer active:scale-[0.98] shadow-sm hover:shadow-md ${isCvMatchActive
                                ? 'bg-violet-500/20 border-violet-400'
                                : 'bg-violet-400/10 border-violet-300'}`}
                            onClick={toggleCvMatching}
                        >
                            <div className="flex items-center justify-between">
                                {/* Left: Premium Badge */}
                                <div className="flex items-center justify-center px-2 py-1 bg-slate-900 rounded-full">
                                    <img
                                        src="/assets/Trouve+white.png"
                                        alt="Trouve+"
                                        className="h-4 w-auto object-contain"
                                    />
                                </div>

                                {/* Center: CV Matching Text */}
                                <span className="text-violet-600 font-bold text-base">
                                    {isEmbeddingUpdating ? 'Mise à jour...' : (isCvMatchActive ? 'Matching Activé ✓' : 'CV Matching')}
                                </span>

                                {/* Right: Info Button */}
                                <div className="relative">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowCvMatchingInfo(!showCvMatchingInfo);
                                        }}
                                        className="focus:outline-none p-1"
                                    >
                                        <Info className={`w-5 h-5 transition-colors ${showCvMatchingInfo ? 'text-violet-600' : 'text-violet-400'}`} />
                                    </button>

                                    {showCvMatchingInfo && (
                                        <div className="absolute bottom-full right-0 mb-3 w-[260px] bg-white rounded-xl p-4 shadow-xl border border-gray-100 z-50 text-left">
                                            <div className="text-xs text-[#64748b] space-y-2 font-medium leading-relaxed">
                                                <p className="font-bold text-violet-600">CV Matching</p>
                                                <p>
                                                    Active le CV Matching pour afficher en priorité les offres de VIE qui correspondent à ton profil, tes envies et ton CV.
                                                </p>
                                                <p className="font-bold text-[#334155]">
                                                    Fonctionnalité Premium Trouve+
                                                </p>
                                            </div>
                                            <div className="absolute -bottom-2 right-2 w-3 h-3 bg-white border-b border-r border-gray-100 transform rotate-45"></div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ========== DESKTOP FILTERS (Horizontal) ========== */}
                {!routeOfferId && (<>
                    <div className="hidden md:flex flex-col lg:flex-row gap-6 mb-8 justify-between items-start lg:items-center">
                        {/* CV Matching Button - Premium Style like Mobile (Slimmer) */}
                        <div className="relative">
                            <div
                                className={`rounded-2xl py-2 px-4 border-2 transition-all cursor-pointer active:scale-[0.98] shadow-sm hover:shadow-md ${isCvMatchActive
                                    ? 'bg-violet-500/20 border-violet-400'
                                    : 'bg-violet-400/10 border-violet-300'}`}
                                onClick={toggleCvMatching}
                            >
                                <div className="flex items-center gap-3">
                                    {/* Premium Badge */}
                                    <div className="flex items-center justify-center px-2.5 py-1 bg-slate-900 rounded-full">
                                        <img
                                            src="/assets/Trouve+white.png"
                                            alt="Trouve+"
                                            className="h-4 w-auto object-contain"
                                        />
                                    </div>

                                    {/* CV Matching Text */}
                                    <span className="text-violet-600 font-bold text-sm">
                                        {isEmbeddingUpdating ? 'Mise à jour...' : (isCvMatchActive ? 'Matching Activé ✓' : 'CV Matching')}
                                    </span>

                                    {/* Info Icon */}
                                    <Info
                                        className={`w-4 h-4 transition-colors ${showCvMatchingInfo ? 'text-violet-600' : 'text-slate-400'}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowCvMatchingInfo(!showCvMatchingInfo);
                                        }}
                                    />
                                </div>
                            </div>

                            {showCvMatchingInfo && (
                                <div className="absolute top-full left-0 mt-4 bg-white p-5 rounded-2xl shadow-xl w-[320px] z-20 border border-gray-100 text-slate-600 text-sm">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="flex items-center justify-center px-2 py-1 bg-slate-900 rounded-full">
                                            <img src="/assets/Trouve+white.png" className="h-4" alt="Trouve+" />
                                        </div>
                                        <span className="font-bold text-violet-600">CV Matching</span>
                                    </div>
                                    <p>Active le CV Matching pour afficher en priorité les offres de VIE qui correspondent à ton profil, tes envies et ton CV.</p>
                                </div>
                            )}
                        </div>

                        {/* Filters */}
                        <div className="flex flex-wrap gap-3 items-center">
                            <ModernMultiSelect
                                value={selectedCountry}
                                options={countryOptions}
                                onChange={handleCountryChange}
                                placeholder="Tous pays"
                                className="min-w-[160px]"
                            />
                            <ModernMultiSelect
                                value={selectedSector}
                                options={sectorOptions}
                                onChange={handleSectorChange}
                                placeholder="Toutes spécialisations"
                                className="min-w-[200px]"
                            />
                            {regions.length > 0 && (
                                <ModernMultiSelect
                                    value={selectedRegion}
                                    options={regionOptions}
                                    onChange={setSelectedRegion}
                                    placeholder="Toutes régions"
                                    className="min-w-[160px]"
                                />
                            )}
                            {durations.length > 0 && (
                                <ModernMultiSelect
                                    value={selectedDuration}
                                    options={durationOptions}
                                    onChange={setSelectedDuration}
                                    placeholder="Toutes durées"
                                    className="min-w-[160px]"
                                />
                            )}
                        </div>

                        {/* Search Bar */}
                        <div className="flex flex-1 justify-end w-full lg:w-auto">
                            <div className="w-full max-w-[400px]">
                                <SearchBox
                                    wrapperClassName="w-full"
                                    inputClassName="w-full pl-11 pr-5 py-3 rounded-full border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-100 text-sm font-medium text-slate-600 placeholder:text-slate-400"
                                    iconClassName="text-slate-400"
                                    placeholder="Rechercher par pays, entreprise, intitulé..."
                                    initialValue={searchQuery}
                                    onSubmit={(term) => updateParams({ search: term })}
                                    onPick={handleSuggestionPick}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Results Count & Top Pagination (Desktop only) */}
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-sm text-slate-500 font-medium">
                            {totalCount} offre{totalCount !== 1 ? 's' : ''} trouvée{totalCount !== 1 ? 's' : ''}
                        </div>
                        {/* Desktop pagination */}
                        {!isMobile && totalPages > 1 && (
                            <ModernPagination
                                currentPage={page}
                                totalPages={totalPages}
                                onPageChange={setPage}
                            />
                        )}
                    </div>
                </>)}

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-13 gap-8 items-start">

                    {/* Left Column: Offers List */}
                    {!routeOfferId && (
                        <div className="lg:col-span-5 flex flex-col gap-4 px-0 md:px-2">
                            {loading ? (
                                <div className="flex flex-col gap-4">
                                    {[1, 2, 3].map(i => (
                                        <div key={i} className="h-32 bg-white/50 rounded-2xl animate-pulse" />
                                    ))}
                                </div>
                            ) : offers.length > 0 ? (
                                <>
                                    {offers.map((offer) => (
                                        <OfferCard
                                            key={offer.id}
                                            offer={offer}
                                            isSelected={selectedOffer?.id === offer.id}
                                            onClick={handleOfferClick}
                                            isFavorite={favorites.includes(offer.id)}
                                            onToggleFavorite={toggleFavorite}
                                            isViewed={viewedOffers.includes(offer.id)}
                                        />
                                    ))}

                                    {/* Infinite scroll sentinel (mobile) */}
                                    {isMobile && hasMore && (
                                        <div ref={sentinelRef} className="py-6 flex justify-center">
                                            {loadingMore && (
                                                <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                                            )}
                                        </div>
                                    )}

                                    {/* Desktop Pagination */}
                                    {!isMobile && totalPages > 1 && (
                                        <div className="flex justify-center py-6">
                                            <ModernPagination
                                                currentPage={page}
                                                totalPages={totalPages}
                                                onPageChange={setPage}
                                            />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="p-8 text-center bg-white rounded-2xl border border-dashed border-gray-200 text-slate-400 space-y-6">
                                    <p>
                                        {searchQuery.trim()
                                            ? <>Aucune offre Business France ne correspond à « {searchQuery.trim()} »</>
                                            : 'Aucune offre ne correspond à votre recherche'}
                                    </p>

                                    {offresPlusTeaser && (
                                        <div className="text-left rounded-2xl bg-gradient-to-br from-violet-50 to-blue-50 border border-violet-200 p-5">
                                            <p className="text-sm font-bold text-violet-700">
                                                {offresPlusTeaser.count} offre{offresPlusTeaser.count > 1 ? 's' : ''} Offres+ correspond{offresPlusTeaser.count > 1 ? 'ent' : ''} à ta recherche
                                            </p>
                                            <p className="text-xs text-slate-500 mt-1 mb-3">
                                                Offres exclusives, non publiées sur Business France.
                                            </p>
                                            <ul className="space-y-1.5 mb-4">
                                                {(offresPlusTeaser.samples.length > 0
                                                    ? offresPlusTeaser.samples
                                                    : Array.from({ length: Math.min(offresPlusTeaser.count, 2) }, () => null)
                                                ).map((o, i) => (
                                                    <li key={i} className="text-sm text-slate-700 truncate">
                                                        {o ? (
                                                            <span>{o.entreprise} · {o.intitule_poste}</span>
                                                        ) : (
                                                            <span className="blur-[4px] select-none">Entreprise · Intitulé du poste masqué</span>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                            <Link
                                                to="/offres-plus"
                                                className="inline-flex items-center gap-2 bg-slate-900 hover:bg-blue-600 text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors"
                                            >
                                                Voir les offres Offres+
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )} {/* End of Left Column Check */}

                    {/* Right Column: Detail View (Desktop only normally, but always if routeOfferId) */}
                    <div className={`${routeOfferId ? 'col-span-full block' : 'hidden lg:block lg:col-span-8 sticky top-24'}`}>
                        {loading ? (
                            <div className="h-[600px] bg-white rounded-2xl animate-pulse" />
                        ) : (
                            <OfferDetailView
                                offer={selectedOffer}
                                isFavorite={selectedOffer ? favorites.includes(selectedOffer.id) : false}
                                onToggleFavorite={toggleFavorite}
                            />
                        )}
                    </div>

                </div>
            </div>

            {/* Mobile Offer Drawer */}
            <MobileOfferDrawer
                isOpen={mobileDrawerOpen}
                onClose={() => setMobileDrawerOpen(false)}
                offer={selectedOffer}
                isFavorite={selectedOffer ? favorites.includes(selectedOffer.id) : false}
                onToggleFavorite={toggleFavorite}
            />
            <PremiumModal
                isOpen={showPremiumModal}
                onClose={() => setShowPremiumModal(false)}
                title="Débloque le CV Matching"
                description="Active le CV Matching pour afficher en priorité les offres de VIE qui correspondent à ton profil, tes envies et ton CV."
                featureName="CV Matching"
            />
        </div>
    );
};

export default OffersPage;
